import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
} from "@fluentui/react-components";
import {
  Delete20Regular,
  Edit24Filled,
  Edit24Regular,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelectedTag } from "../../contexts/SelectedTagContext";
import { useTagsDelete } from "../../hooks/useTags";
import { DeleteTagRequest, FlatVsaaTag } from "../../models/VsaaTag";
import { SkanskaColorTokens } from "../../style/colors";
import CopyTagsPanel from "./CopyTagsPanel";
import { EditTreeItemDialog } from "./EditTreeItemDialog";
import RearrangeTagsPanel from "./RearrangeTagsPanel";
import SortTagsPanel from "./SortTagsPanel";

interface TagOverviewPanel {
  tagCollectionId: string;
  tags: FlatVsaaTag[];
  allTags: FlatVsaaTag[];
  onProcessingTags: (isProcessingTags: boolean) => void;
}

const useStyles = makeStyles({
  tagOverviewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  tagOverview: {
    display: "flex",
    flexDirection: "row",
    paddingBottom: "10px",
    borderBottom: "1px solid #ccc",
    justifyContent: "space-between",
  },
  titleEdit: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  rearrangePanelContainer: {
    margin: "20px 0",
    paddingBottom: "20px",
    borderBottom: "1px solid #ccc",
  },
  icons: {
    cursor: "pointer",
    marginLeft: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sortPanelContainer: {
    paddingBottom: "20px",
    borderBottom: "1px solid #ccc",
  },
  confirmDeleteButton: {
    backgroundColor: SkanskaColorTokens.color_tertiary_red_30,
    ":hover, :focus, :active": {
      backgroundColor: SkanskaColorTokens.color_tertiary_red,
      color: SkanskaColorTokens.color_secondary_white,
    },
  },
});
export const TagOverviewPanel = (props: TagOverviewPanel) => {
  const { tagCollectionId, tags, allTags, onProcessingTags } = props;
  const tagContext = useSelectedTag();
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editDialogTitle, seteditDialogTitle] = useState<string>("");
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [iconHover, setIconHover] = useState(false);
  const { mutateAsync: deleteTag } = useTagsDelete(tagCollectionId);
  const [isRearranging, setIsRearranging] = useState<boolean>(false);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [isSorting, setIsSorting] = useState<boolean>(false);

  const classes = useStyles();
  const { t } = useTranslation();
  const onEditSave = () => {
    setEditDialogOpen(false);
  };

  const onOpenEditDialog = () => {
    const dialogTitle = `Editera titeln på ${tagContext?.selectedTag?.title}`;
    seteditDialogTitle(dialogTitle);
    setEditDialogOpen(true);
  };

  const handleEditClick = () => {
    onOpenEditDialog();
  };

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!tagContext?.selectedTag) {
      return;
    }
    const deleteRequest: DeleteTagRequest = {
      id: tagContext.selectedTag.id,
      ancestorIds: tagContext.selectedTag.ancestorIds,
    };
    await deleteTag(deleteRequest);
    setDeleteDialogOpen(false);
    tagContext.setSelectedTag(undefined);
  }, [tagContext, deleteTag]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  useEffect(() => {
    onProcessingTags(isRearranging || isCopying || isSorting);
  }, [isRearranging, isCopying, onProcessingTags, isSorting]);

  return (
    <>
      <div className={classes.tagOverviewContainer}>
        <div className={classes.tagOverview}>
          <div className={classes.titleEdit}>
            <h3> {tagContext?.selectedTag?.title}</h3>
            <div
              className={classes.icons}
              onMouseEnter={() => setIconHover(true)}
              onMouseLeave={() => setIconHover(false)}
              onClick={handleEditClick}
            >
              {iconHover ? <Edit24Filled /> : <Edit24Regular />}
            </div>
          </div>
          {tagContext?.selectedTag?.parentId && (
            <Button
              className={classes.confirmDeleteButton}
              onClick={handleDeleteClick}
            >
              <Delete20Regular />
              <span>{t("delete")}</span>
            </Button>
          )}
        </div>
        <div className={classes.sortPanelContainer}>
          <SortTagsPanel
            tagCollectionId={tagCollectionId}
            tags={tags}
            onIsSorting={setIsSorting}
          />
        </div>
        {tagContext?.selectedTag?.parentId && (
          <div className={classes.rearrangePanelContainer}>
            <RearrangeTagsPanel
              tagCollectionId={tagCollectionId}
              allTags={allTags}
              onIsRearranging={setIsRearranging}
            />
          </div>
        )}
        {tagContext?.selectedTag?.parentId && (
          <div className={classes.rearrangePanelContainer}>
            <CopyTagsPanel
              tagCollectionId={tagCollectionId}
              allTags={allTags}
              onIsCopying={setIsCopying}
            />
          </div>
        )}
      </div>

      <EditTreeItemDialog
        key={editDialogOpen ? tagContext?.selectedTag?.id : "closed"}
        onClose={() => setEditDialogOpen(false)}
        onSave={onEditSave}
        tagCollectionId={tagCollectionId}
        title={editDialogTitle}
        open={editDialogOpen}
        id={tagContext?.selectedTag?.id}
        ancestorIds={tagContext?.selectedTag?.ancestorIds}
        inputValue={tagContext?.selectedTag?.title}
        allTags={allTags}
      />
      {isDeleteDialogOpen && (
        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{t("delete")}</DialogTitle>
              <DialogContent>
                {t("deleteTagPrompt")}
                {tagContext?.selectedTag?.title + `?`} {t("deleteTagWarning")}
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" onClick={handleCancelDelete}>
                  {t("cancel")}
                </Button>
                <Button
                  className={classes.confirmDeleteButton}
                  onClick={handleConfirmDelete}
                >
                  {t("delete")}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
};
